"use client";

import {
	ArrowLeft,
	ArrowRight,
	ArrowUp,
	ChevronLeft,
	ChevronRight,
} from "lucide-react";
import { useReducedMotion } from "motion/react";
import React, {
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { flushSync } from "react-dom";

import { cn } from "../../lib";
import { CircleButton } from "../custom/circle-button";
import { GLASS_BORDER_RING_WIDTH_PX } from "../custom/glass-border-ring";

import {
	type MagicCardFlag,
	MagicCardListCornerFlag,
	type MagicCardListCornerFlagPlacement,
} from "./magic-tile-corner-flag";
import { MagicTorch, MagicTorchSurface } from "./magic-torch";

/**
 * In-flow aura square (glass-inset) so card min-height stays portrait.
 * Text pins to the bottom with negative margin and may overlap the lower glow.
 */
/** Outer shell radius (matches torch / card chrome). */
const MAGIC_CARD_CLIP_RADIUS_CLASS =
	"rounded-[var(--magic-torch-radius,var(--magic-card-radius,1.5rem))]";

const MAGIC_CARD_GLASS_INSET_PX = GLASS_BORDER_RING_WIDTH_PX;

/** Inner plate / body radius — must stay inside the frosted ring. */
const MAGIC_CARD_INNER_RADIUS_CSS = `calc(var(--magic-torch-radius, var(--magic-card-radius, 1.5rem)) - ${MAGIC_CARD_GLASS_INSET_PX}px)`;

/**
 * Clip opaque fill to the glass inner edge. Inline styles (not Tailwind
 * arbitrary classes) so radius always applies — interpolated class names
 * are easy for JIT to miss, which left square corners over the ring.
 */
const MAGIC_CARD_INNER_CLIP_STYLE = {
	margin: MAGIC_CARD_GLASS_INSET_PX,
	borderRadius: MAGIC_CARD_INNER_RADIUS_CSS,
	overflow: "hidden",
	clipPath: `inset(0 round ${MAGIC_CARD_INNER_RADIUS_CSS})`,
	WebkitClipPath: `inset(0 round ${MAGIC_CARD_INNER_RADIUS_CSS})`,
} as const satisfies React.CSSProperties;

const MAGIC_CARD_PLATE_STYLE = {
	position: "absolute",
	inset: MAGIC_CARD_GLASS_INSET_PX,
	borderRadius: MAGIC_CARD_INNER_RADIUS_CSS,
	overflow: "hidden",
	clipPath: `inset(0 round ${MAGIC_CARD_INNER_RADIUS_CSS})`,
	WebkitClipPath: `inset(0 round ${MAGIC_CARD_INNER_RADIUS_CSS})`,
	zIndex: 0,
} as const satisfies React.CSSProperties;

/** Extra lift for the AuraBlob hero (px). */
const MAGIC_CARD_AURA_LIFT_PX = 40;

function MagicCardAuraHeroBand({
	hero,
	disabled = false,
}: {
	hero: React.ReactNode;
	disabled?: boolean;
}) {
	return (
		<div
			className={cn(
				"relative w-auto shrink-0 overflow-hidden",
				disabled && "opacity-50",
			)}
			style={{
				marginTop: -MAGIC_CARD_AURA_LIFT_PX,
			}}
			data-sq-magic-card-aura-band=""
		>
			{/*
			 * Aspect-square in normal flow → card height tracks width again.
			 * Absolute canvas fill keeps the sphere centered (incl. fan rotate).
			 */}
			<div className="relative aspect-square w-full">
				<div className="absolute inset-0">{hero}</div>
			</div>
		</div>
	);
}

function MagicCardHeroTextBlock({
	title,
	titleClassName,
	subtitle,
	subtitleClassName,
	detail,
	detailClassName,
	badges,
}: {
	title: string;
	titleClassName: string;
	subtitle: string;
	subtitleClassName: string;
	detail: React.ReactNode;
	detailClassName?: string;
	badges: readonly MagicCardBadge[] | undefined;
}) {
	return (
		<div className="relative z-[2] -mt-16 flex w-full shrink-0 flex-col gap-2 px-5 pb-5 pt-8 sm:-mt-20 sm:pt-10">
			{/*
			 * Scrim so title/description/detail stay legible when overlapping
			 * the aura. Matches magic-card surface fills.
			 */}
			<div
				className="pointer-events-none absolute inset-x-0 bottom-0 top-0 -z-[1] bg-gradient-to-t from-white from-50% via-white/92 to-transparent dark:from-[#141924] dark:via-[#141924]/92"
				aria-hidden
			/>
			<p className={titleClassName}>{title}</p>
			<p className={subtitleClassName}>{subtitle}</p>
			{detail != null ? (
				<div className={detailClassName}>{detail}</div>
			) : null}
			<MagicCardBadgeRow badges={badges} />
		</div>
	);
}

export interface MagicCardBadge {
	label: string;
	/** Optional leading node (e.g. Lucide icon for duration / stops). */
	icon?: React.ReactNode;
}

export interface MagicCardImage {
	/** Public path or absolute URL. */
	src: string;
	alt: string;
}

/**
 * Caller-only metadata for hub / wizard flows.
 * **`MagicCards` does not read `aiIntent`** — it is not passed to layout or handlers; keep using `onCardActivate` + app state for behavior.
 */
export interface MagicCardAiIntent {
	/** Optional user message the parent may send when the card is activated. */
	userMessage?: string;
	/** Optional alternative when the active chat already has history (parent decides). */
	userMessageInstead?: string;
	/** Optional post-pick headline copy for parents that animate headings themselves. */
	afterPickHeadline?: string;
	afterPickSubline?: string;
	afterPickHint?: string;
}

export interface MagicCardProps {
	/** Stable key/id for focus + ordering. */
	id: string;
	/** Small uppercase label (e.g. DISCOVER / TOPIC). */
	label: string;
	/** Main title. If omitted, `label` is used as the title. */
	title?: string;
	/** Subtitle/description line(s). */
	subtitle: string;
	/** Optional pill badges shown under the subtitle. */
	badges?: readonly MagicCardBadge[];
	/** Optional top-right chrome (e.g. duration range badge). */
	topRight?: React.ReactNode;
	/** Optional hero image (mirrors hub tiles: beside title when narrow, above when wide). */
	image?: MagicCardImage;
	/**
	 * Optional hero media node (e.g. AuraBlob). When set, replaces `image` in the hero slot.
	 */
	hero?: React.ReactNode;
	/** Optional line under the subtitle (e.g. quantified boost effect). */
	detail?: React.ReactNode;
	/**
	 * Optional per-card glow override (comma-separated RGB). Falls back to
	 * {@link MagicCardsProps.glowColor} / `glowColorDark`.
	 */
	glowColor?: string;
	glowColorDark?: string;
	/** Optional metadata for hub click flows (chat + post-pick copy). */
	aiIntent?: MagicCardAiIntent;
	/** Optional href for callers who want to navigate. */
	href?: string;
	/** Optional extra payload for callers. */
	payload?: unknown;
	/** When true, the card does not activate and skips hover motion / image & title ramp. */
	disabled?: boolean;
	/** When true, shows a loading veil + spinner and prevents activation. */
	loading?: boolean;
	/** Optional absolute overlay content (rendered on top of the card). */
	overlay?: React.ReactNode;
	/** Optional list-style corner flags (same geometry as org entity list tiles). */
	flags?: readonly MagicCardFlag[];
	/**
	 * After the hub deck collapses on a pick, parent shells may show this prompt above the cards.
	 * `staticText` is the fixed prefix; `rotatingTexts` cycles through the trailing clause (e.g. subject nouns).
	 */
	hubPrompt?: {
		staticText: string;
		rotatingTexts: readonly string[];
	};
}

function MagicCardLoadingOverlay() {
	return (
		<div
			className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center rounded-3xl bg-white/20 backdrop-blur-sm dark:bg-black/20"
			aria-hidden
		>
			<div className="h-9 w-9 animate-spin rounded-full border-2 border-gray-300 border-t-gray-800 dark:border-white/20 dark:border-t-white/80" />
		</div>
	);
}

export interface MagicCardsProps {
	cards: readonly MagicCardProps[];
	/**
	 * `hub` (default) = Learn/Create fan deck.
	 * `settingsCompact` = 3-up grid @ ~375px (Phase 6 / Train D); 6 options → 2×3.
	 */
	density?: "hub" | "settingsCompact";
	/**
	 * Ribbon axis for hub density (ignored by `settingsCompact`).
	 * - `horizontal` (default): fan/deck at **all** breakpoints; card chrome scales via
	 *   `wide` / `medium` / `compact` size tiers from container width.
	 * - `vertical`: legacy narrow column stack below the wide fan threshold.
	 */
	ribbonAxis?: "horizontal" | "vertical";
	/** When true, dim other cards after one is selected (until `focusResetKey` changes). */
	dimUnselectedOnClick?: boolean;
	/** Called when a card is activated. Caller handles routing + chat side-effects. */
	onCardActivate?: (index: number, card: MagicCardProps) => void;
	/**
	 * When this value changes, fan/deck layout and dimming reset (e.g. hub SWITCH increments a nonce).
	 * Do not key this to booleans that flip twice per flow (e.g. force-picker true→false after a timeout) or you will undo a tile pick mid-window.
	 * Do not key to per-tile selection — that can cancel the same-click deck collapse.
	 */
	focusResetKey?: string;

	/** Enable the global torchlight spotlight over the whole section. */
	enableSpotlight?: boolean;
	/** Enable glowing border highlight under spotlight. */
	enableBorderGlow?: boolean;
	/** Enable hover tilt (disabled on mobile by default). */
	enableTilt?: boolean;
	/** Enable magnetism (subtle follow cursor). */
	enableMagnetism?: boolean;
	/** Enable optional particles (stars) like MagicBento. */
	enableParticles?: boolean;
	particleCount?: number;
	/** Disable animations (useful for reduced motion or perf). */
	disableAnimations?: boolean;

	/**
	 * Comma-separated RGB (e.g. `217, 152, 0`) — use a darker value for good contrast on light cards.
	 */
	glowColor?: string;
	/** Comma-separated RGB for dark card surfaces. */
	glowColorDark?: string;
	spotlightRadius?: number;
	/** Applied to the outer wrapper — use `flex-1 min-h-0` when filling a flex parent’s remaining height. */
	className?: string;
	/**
	 * Increment when the hub “Switch” (or equivalent) should fan cards back out from deck mode.
	 * Only applies when already collapsed to deck + wide fan breakpoint.
	 */
	fanExpandKey?: number;
	/**
	 * Increment to collapse the fan into a stacked deck (center card) without
	 * calling `onCardActivate`. Same CSS morph as picking a card. Used for
	 * ritual transitions (e.g. boost reroll) via {@link useMagicCardsDeckShuffleControls}.
	 */
	deckCollapseKey?: number;
	/**
	 * When set to a card `id`, collapses the deck to that tile once per distinct value (e.g. `?mode=` deeplink).
	 * Does **not** call `onCardActivate`. Suppressed after the user fans via `focusResetKey` until this id changes.
	 */
	initialCollapsedSelectionId?: string | null;
	/**
	 * When the collapsed deck focus changes (pick / fan reset), receives the visible card or null.
	 * Used by hub shells for post-pick chrome (e.g. rotating prompt lines).
	 */
	onDeckFocusChange?: (card: MagicCardProps | null) => void;
	/**
	 * Edge CircleButtons on fan cards: left on leftmost, right on rightmost,
	 * up on the middle card when there are exactly three. Hover pops the
	 * control outward along the arrow. Hidden while the deck is collapsed.
	 */
	enableEdgeSelectHints?: boolean;
	/**
	 * ArrowLeft / ArrowRight / ArrowUp select matching edge cards.
	 * Defaults to on when `enableEdgeSelectHints` is true.
	 * Suppressed while compact/medium carousel chevrons are shown — those
	 * keys navigate the carousel instead.
	 */
	enableArrowKeySelect?: boolean;
	/**
	 * Compact/medium overlapping fans show under-card carousel chevrons.
	 * `infinite` (default) loops with slot wrap; `clamp` stops at first/last.
	 */
	carouselMode?: "clamp" | "infinite";
}

/**
 * Wide fan size tier when the section’s inline size is at least this many CSS pixels.
 * Also the legacy threshold for `ribbonAxis="vertical"` → horizontal fan.
 */
const MAGIC_CARDS_FAN_MIN_PX = 960;
/** Medium horizontal-fan tier (between compact and wide). */
const MAGIC_CARDS_FAN_MEDIUM_MIN_PX = 560;

export type MagicCardsFanSize = "wide" | "medium" | "compact";

export function resolveMagicCardsFanSize(
	containerWidthPx: number,
): MagicCardsFanSize {
	if (containerWidthPx >= MAGIC_CARDS_FAN_MIN_PX) {
		return "wide";
	}
	if (containerWidthPx >= MAGIC_CARDS_FAN_MEDIUM_MIN_PX) {
		return "medium";
	}
	return "compact";
}

export type MagicCardsEdgeSelectHint = "left" | "right" | "up";

/**
 * Fan edge affordances: extremes get left/right; exactly-three fans also get up
 * on the middle card.
 */
export function resolveMagicCardsEdgeSelectHint(
	index: number,
	count: number,
): MagicCardsEdgeSelectHint | null {
	if (count < 2 || index < 0 || index >= count) {
		return null;
	}
	if (index === 0) {
		return "left";
	}
	if (index === count - 1) {
		return "right";
	}
	if (count === 3 && index === 1) {
		return "up";
	}
	return null;
}

function edgeSelectHintIndex(
	hint: MagicCardsEdgeSelectHint,
	count: number,
): number | null {
	if (count < 2) {
		return null;
	}
	if (hint === "left") {
		return 0;
	}
	if (hint === "right") {
		return count - 1;
	}
	if (hint === "up" && count === 3) {
		return 1;
	}
	return null;
}

/** Default middle-slot card index for a fan of `count` cards. */
export function resolveMagicCardsCarouselCenterIndex(count: number): number {
	if (count <= 1) {
		return 0;
	}
	return Math.floor((count - 1) / 2);
}

/**
 * Maps a data-order card index into a fan visual slot so `centerIndex` sits
 * in the middle slot (highest z). Used by compact/medium carousel.
 */
export function resolveMagicCardsCarouselVisualSlot(
	dataIndex: number,
	centerIndex: number,
	count: number,
): number {
	if (count <= 0) {
		return 0;
	}
	const mid = resolveMagicCardsCarouselCenterIndex(count);
	return (((dataIndex - centerIndex + mid) % count) + count) % count;
}

/** Next carousel center after a ±1 step (`infinite` default / `clamp`). */
export function stepMagicCardsCarouselCenter(
	centerIndex: number,
	count: number,
	delta: -1 | 1,
	mode: "clamp" | "infinite" = "infinite",
): number {
	if (count <= 1) {
		return 0;
	}
	if (mode === "infinite") {
		return (((centerIndex + delta) % count) + count) % count;
	}
	return Math.min(count - 1, Math.max(0, centerIndex + delta));
}

/** Compact/medium overlapping fans show under-card carousel chevrons. */
export function resolveMagicCardsCarouselNavVisible(
	fanSize: MagicCardsFanSize,
	cardCount: number,
	deckCollapsed: boolean,
): boolean {
	return (
		!deckCollapsed &&
		cardCount >= 2 &&
		(fanSize === "compact" || fanSize === "medium")
	);
}

/** Vertical arc depth for fanned pose (collapsed deck uses the midpoint of this curve). */
const ARC_PEAK_PX = 16;
/** CSS-only fan ↔ deck morph on `.magic-card-transform-layer` (wide layout). */
const DECK_TRANSFORM_MS = 920;
const DECK_TRANSFORM_MS_REDUCED = 140;
/** Compact/medium carousel slot shuffle — on-screen morph, keep under 300ms. */
const CAROUSEL_TRANSFORM_MS = 260;
const CAROUSEL_TRANSFORM_MS_REDUCED = 120;
const CAROUSEL_TRANSFORM_EASE = "cubic-bezier(0.77, 0, 0.175, 1)";
/** Hover raise above fan stack + edge hint buttons. */
const FAN_HOVER_Z_INDEX = 80;

/** Keep org hub “Switch” row motion in sync with fan↔deck CSS on `.magic-card-transform-layer`. */
export const MAGIC_CARDS_DECK_TRANSFORM_MS = DECK_TRANSFORM_MS;
export const MAGIC_CARDS_DECK_TRANSFORM_MS_REDUCED = DECK_TRANSFORM_MS_REDUCED;
/** Cubic-bezier tuple for Motion `ease` — matches `cubic-bezier(0.22, 1, 0.36, 1)` in this file’s transform transition. */
export const MAGIC_CARDS_DECK_TRANSFORM_EASE = [0.22, 1, 0.36, 1] as const;
/** Approximates vertical pitch (card block + gap) for narrow-stack → deck morph start pose. */
const NARROW_COLUMN_MORPH_PITCH_PX = 228;

const DEFAULT_PARTICLE_COUNT = 12;
const DEFAULT_SPOTLIGHT_RADIUS = 320;
/** Deeper purple for light / white card backgrounds (higher contrast). */
const DEFAULT_GLOW_COLOR = "100, 0, 200";
/** Brighter purple for dark card backgrounds (legacy single-`glowColor` default). */
const DEFAULT_GLOW_COLOR_DARK = "132, 0, 255";
/** Neutral glow when a card is disabled (light mode). */
const DISABLED_GLOW_COLOR = "210, 210, 210";
/** Neutral glow when a card is disabled (dark mode). */
const DISABLED_GLOW_COLOR_DARK = "120, 120, 120";
const MOBILE_BREAKPOINT = 768;

function useMobileDetection() {
	const [isMobile, setIsMobile] = useState(false);
	useEffect(() => {
		const checkMobile = () =>
			setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
		checkMobile();
		window.addEventListener("resize", checkMobile);
		return () => window.removeEventListener("resize", checkMobile);
	}, []);
	return isMobile;
}

/** Matches `next-themes` / Tailwind: `class="dark"` on `documentElement`. */
function useDocumentDarkClass(): boolean {
	const [isDark, setIsDark] = useState(false);

	useEffect(() => {
		if (typeof document === "undefined") {
			return;
		}
		const root = document.documentElement;
		const read = () => {
			setIsDark(root.classList.contains("dark"));
		};
		read();
		const obs = new MutationObserver(read);
		obs.observe(root, { attributes: true, attributeFilter: ["class"] });
		return () => {
			obs.disconnect();
		};
	}, []);

	return isDark;
}

function MagicCardBadgeRow({ badges }: { badges?: readonly MagicCardBadge[] }) {
	if (!badges?.length) {
		return null;
	}
	return (
		<div className="mt-3 flex min-w-0 flex-nowrap items-center gap-1.5 overflow-hidden @[60rem]:mt-1">
			{badges.map((b, i) => (
				<span
					key={`${b.label}-${i}`}
					className="inline-flex h-6 min-h-6 shrink-0 items-center justify-center gap-1 whitespace-nowrap rounded-full border border-gray-200 bg-white/70 px-2 py-0 text-[10px] font-black uppercase leading-none tracking-wide text-gray-800 dark:border-white/10 dark:bg-white/5 dark:text-white/85"
				>
					{b.icon ? (
						<span className="inline-flex shrink-0 items-center justify-center [&_svg]:size-3 [&_svg]:shrink-0">
							{b.icon}
						</span>
					) : null}
					<span className="inline-flex items-center leading-none">
						{b.label}
					</span>
				</span>
			))}
		</div>
	);
}

/** Matches `magic-bento` `card__label` copy — muted base; hover / persisted active share the strong tone. */
const MAGIC_CARD_TITLE_INTERACTIVE_COLORS =
	"uppercase tracking-wide text-gray-300 transition-colors duration-300 group-hover:text-gray-800 dark:text-white/30 dark:group-hover:text-white/70 group-data-[active]:text-gray-800 dark:group-data-[active]:text-white/70";

/** Frozen label/image treatment — must not use `group-hover` / `group-data-[active]` so disabled tiles don’t react under hover. */
const MAGIC_CARD_TITLE_DISABLED_COLORS =
	"uppercase tracking-wide text-gray-300 dark:text-white/30";

const MAGIC_CARD_IMG_INTERACTIVE =
	"magic-card-hover-colorize magic-card-img-fade opacity-50 transition-[filter,opacity] duration-300 ease-out group-hover:opacity-100 group-data-[active]:opacity-100 group-focus-visible:opacity-100";

const MAGIC_CARD_IMG_DISABLED = "opacity-50 grayscale transition-none";

/**
 * Grayscale by default; full color on card hover / focus, or when the card is the
 * carousel center / persisted active and no other card is hovered.
 * Pair with MagicCards CSS (`.magic-card-hover-colorize`).
 */
export const MAGIC_CARD_HOVER_COLORIZE_CLASS = "magic-card-hover-colorize";

/**
 * Idle at pulse-scale min; animates only under the same hover / carousel-active
 * conditions as {@link MAGIC_CARD_HOVER_COLORIZE_CLASS}.
 * Pair with MagicCards CSS (`.magic-card-pulse-scale`).
 */
export const MAGIC_CARD_PULSE_SCALE_CLASS = "magic-card-pulse-scale";

/**
 * Intra-card layout aligned with SectionHero hub tiles (Create hub reference):
 * duration top-right; hero image + title/description/badge stack using the same breakpoints.
 */
function MagicCardFace({
	card,
	title,
	layoutVariant,
	flagPlacement,
	fanSize,
	overlay,
	blurContent,
	showCornerFlag = true,
}: {
	card: MagicCardProps;
	title: string;
	/** `fan`: hub row — portrait column inside each narrow tile. `stack`: vertically stacked hub — side‑by‑side 30/70 split (until ultra‑wide card CQ). */
	layoutVariant: "fan" | "stack";
	/** Ribbon geometry: wide horizontal fan vs narrow stacked column (`MagicCards` container width). */
	flagPlacement: MagicCardListCornerFlagPlacement;
	fanSize: MagicCardsFanSize;
	overlay?: React.ReactNode;
	/** Only blur card contents for specific overlays (e.g. GET CREDITS), not for loading spinners. */
	blurContent?: boolean;
	/**
	 * When false, omit the FlagBanner (host paints it as a sibling above glass ring).
	 * Still keeps fan `overflow-visible` when a flag exists.
	 */
	showCornerFlag?: boolean;
}) {
	const src = card.image?.src;
	const hasHero = card.hero != null || Boolean(src);
	const disabled = Boolean(card.disabled);
	const cornerFlag = card.flags?.[0];
	const surfaceOverflow =
		cornerFlag && flagPlacement === "fan"
			? "overflow-visible"
			: "overflow-hidden";
	const renderFlag = showCornerFlag && cornerFlag != null;
	const dimBody = disabled ? "opacity-30" : "";
	const hasHeroNode = card.hero != null;
	const titleTone = disabled
		? MAGIC_CARD_TITLE_DISABLED_COLORS
		: hasHeroNode
			? "uppercase tracking-wide text-foreground"
			: MAGIC_CARD_TITLE_INTERACTIVE_COLORS;
	const subtitleTone = cn(
		"min-w-0 break-words text-sm leading-relaxed line-clamp-2 min-h-[2lh]",
		"text-muted-foreground dark:text-foreground/80",
	);
	const detailTone =
		"min-w-0 text-sm leading-snug text-foreground dark:text-foreground/95";
	const imgInteractive = disabled
		? MAGIC_CARD_IMG_DISABLED
		: MAGIC_CARD_IMG_INTERACTIVE;
	const imgToneFan = cn(
		"min-h-[84px] max-h-[150px] w-auto max-w-full object-contain",
		imgInteractive,
	);
	const imgToneStack = cn(
		"min-h-[72px] max-h-[114px] w-auto max-w-full object-contain @[60rem]:min-h-[84px] @[60rem]:max-h-[150px]",
		imgInteractive,
	);
	const blurClassName = blurContent ? "blur-[6px]" : "";
	/** Wide fan: keep long labels (e.g. RETURN TO MAP) on one line. */
	const fanTitleSizeClass =
		fanSize === "wide"
			? "text-xl leading-tight"
			: fanSize === "medium"
				? "text-2xl leading-tight"
				: "text-xl leading-tight";
	const stackTitleSizeClass = "text-2xl @[48rem]:text-3xl leading-tight";

	const heroMediaFan = card.hero ? (
		<MagicCardAuraHeroBand hero={card.hero} disabled={disabled} />
	) : src ? (
		<img
			src={src}
			alt={card.image?.alt ?? ""}
			className={imgToneFan}
			loading="lazy"
		/>
	) : null;

	const heroMediaStack = card.hero ? (
		<MagicCardAuraHeroBand hero={card.hero} disabled={disabled} />
	) : src ? (
		<img
			src={src}
			alt={card.image?.alt ?? ""}
			className={imgToneStack}
			loading="lazy"
		/>
	) : null;

	if (!hasHero) {
		return (
			<div
				className={cn(
					"magic-card__surface relative flex min-h-0 h-full w-full flex-1 flex-col justify-end gap-3 text-left",
					MAGIC_CARD_CLIP_RADIUS_CLASS,
					surfaceOverflow,
				)}
			>
				{renderFlag ? (
					<MagicCardListCornerFlag
						flag={cornerFlag}
						placement={flagPlacement}
					/>
				) : null}
				{overlay ? overlay : null}
				<div
					className={cn(
						"relative flex min-h-0 min-w-0 flex-1 flex-col justify-end gap-1.5 px-5 pb-5 pt-5",
						dimBody,
						blurClassName,
					)}
				>
					{card.topRight ? (
						<span className="pointer-events-none absolute top-5 right-5 z-[3]">
							{card.topRight}
						</span>
					) : null}
					<p
						className={cn(
							"magic-card__title min-w-0 break-words font-black",
							layoutVariant === "fan"
								? fanTitleSizeClass
								: stackTitleSizeClass,
							titleTone,
						)}
					>
						{title}
					</p>
					<p className={subtitleTone}>{card.subtitle}</p>
					{card.detail != null ? (
						<div className={detailTone}>{card.detail}</div>
					) : null}
					<MagicCardBadgeRow badges={card.badges} />
				</div>
			</div>
		);
	}

	if (layoutVariant === "fan") {
		if (hasHeroNode) {
			return (
				<div
					className={cn(
						"magic-card__surface relative flex h-full min-h-0 max-h-full w-full flex-1 flex-col items-stretch gap-0 text-left",
						MAGIC_CARD_CLIP_RADIUS_CLASS,
						surfaceOverflow,
					)}
				>
					{renderFlag ? (
						<MagicCardListCornerFlag
							flag={cornerFlag}
							placement={flagPlacement}
						/>
					) : null}
					{overlay ? overlay : null}
					<div
						className={cn(
							"relative flex min-h-0 min-w-0 flex-1 flex-col items-stretch",
							dimBody,
							blurClassName,
						)}
						style={MAGIC_CARD_INNER_CLIP_STYLE}
					>
						{card.topRight ? (
							<span className="pointer-events-none absolute top-5 right-5 z-[3]">
								{card.topRight}
							</span>
						) : null}
						{heroMediaFan}
						<MagicCardHeroTextBlock
							title={title}
							titleClassName={cn(
								"magic-card__title min-w-0 break-words font-black",
								fanTitleSizeClass,
								titleTone,
							)}
							subtitle={card.subtitle}
							subtitleClassName={subtitleTone}
							detail={card.detail}
							detailClassName={cn(detailTone, "pt-0.5")}
							badges={card.badges}
						/>
					</div>
				</div>
			);
		}

		return (
			<div
				className={cn(
					"magic-card__surface relative flex h-full min-h-0 max-h-full w-full flex-1 flex-col items-stretch gap-0 text-left",
					MAGIC_CARD_CLIP_RADIUS_CLASS,
					surfaceOverflow,
				)}
			>
				{renderFlag ? (
					<MagicCardListCornerFlag
						flag={cornerFlag}
						placement={flagPlacement}
					/>
				) : null}
				{overlay ? overlay : null}
				<div
					className={cn(
						"relative flex min-h-0 min-w-0 flex-1 flex-col items-stretch gap-0 px-5 pb-5 pt-5",
						dimBody,
						blurClassName,
					)}
				>
					{card.topRight ? (
						<span className="pointer-events-none absolute top-5 right-5 z-[3]">
							{card.topRight}
						</span>
					) : null}

					<div className="relative flex min-h-0 min-w-0 flex-1 flex-col items-center justify-center py-2">
						{src ? (
							<img
								src={src}
								alt={card.image?.alt ?? ""}
								className={imgToneFan}
								loading="lazy"
							/>
						) : null}
					</div>

					<div className="flex shrink-0 flex-col gap-1.5">
						<p
							className={cn(
								"magic-card__title min-w-0 break-words font-black",
								fanTitleSizeClass,
								titleTone,
							)}
						>
							{title}
						</p>
						<p className={subtitleTone}>{card.subtitle}</p>
						{card.detail != null ? (
							<div className={detailTone}>{card.detail}</div>
						) : null}
						<MagicCardBadgeRow badges={card.badges} />
					</div>
				</div>
			</div>
		);
	}

	if (hasHeroNode) {
		return (
			<div
				className={cn(
					"magic-card__surface relative flex h-full min-h-0 max-h-full w-full flex-1 flex-col text-left",
					MAGIC_CARD_CLIP_RADIUS_CLASS,
					surfaceOverflow,
				)}
			>
				{renderFlag ? (
					<MagicCardListCornerFlag
						flag={cornerFlag}
						placement={flagPlacement}
					/>
				) : null}
				{overlay ? overlay : null}
				<div
					className={cn(
						"relative flex min-h-0 w-full flex-1 flex-col",
						dimBody,
						blurClassName,
					)}
					style={MAGIC_CARD_INNER_CLIP_STYLE}
				>
					{card.topRight ? (
						<span className="pointer-events-none absolute top-5 right-5 z-[3]">
							{card.topRight}
						</span>
					) : null}
					{heroMediaStack}
					<MagicCardHeroTextBlock
						title={title}
						titleClassName={cn(
							"magic-card__title min-w-0 break-words font-black",
							stackTitleSizeClass,
							titleTone,
						)}
						subtitle={card.subtitle}
						subtitleClassName={subtitleTone}
						detail={card.detail}
						detailClassName={cn(detailTone, "pt-0.5")}
						badges={card.badges}
					/>
				</div>
			</div>
		);
	}

	return (
		<div
			className={cn(
				"magic-card__surface relative flex h-full min-h-0 max-h-full w-full flex-1 flex-col text-left",
				MAGIC_CARD_CLIP_RADIUS_CLASS,
				surfaceOverflow,
			)}
		>
			{renderFlag ? (
				<MagicCardListCornerFlag
					flag={cornerFlag}
					placement={flagPlacement}
				/>
			) : null}
			{overlay ? overlay : null}
			<div
				className={cn(
					"relative grid min-h-0 w-full flex-1 grid-cols-[3fr_7fr] items-stretch gap-3 text-left",
					"@[60rem]:flex @[60rem]:flex-col @[60rem]:items-stretch @[60rem]:gap-0",
					"px-5 pb-5 pt-5",
					dimBody,
					blurClassName,
				)}
			>
				{card.topRight ? (
					<span className="pointer-events-none absolute top-5 right-5 z-[3]">
						{card.topRight}
					</span>
				) : null}

				<div className="relative flex min-h-0 min-w-0 items-center justify-center @[60rem]:flex-1 @[60rem]:pb-3 @[60rem]:pt-8">
					{src ? (
						<img
							src={src}
							alt={card.image?.alt ?? ""}
							className={imgToneStack}
							loading="lazy"
						/>
					) : null}
				</div>

				<div className="flex min-h-0 min-w-0 flex-col justify-center gap-1.5 pr-2 @[60rem]:shrink-0 @[60rem]:space-y-1.5 @[60rem]:pr-0">
					<p
						className={cn(
							"magic-card__title min-w-0 break-words font-black",
							stackTitleSizeClass,
							titleTone,
						)}
					>
						{title}
					</p>
					<p className={subtitleTone}>{card.subtitle}</p>
					{card.detail != null ? (
						<div className={detailTone}>{card.detail}</div>
					) : null}
					<MagicCardBadgeRow badges={card.badges} />
				</div>
			</div>
		</div>
	);
}

/** Edge CircleButton — centered on the card edge; pops outward on card hover. */
function MagicCardEdgeSelectHint({
	direction,
	disabled,
	reducedMotion,
	onActivate,
}: {
	direction: MagicCardsEdgeSelectHint;
	disabled?: boolean;
	reducedMotion?: boolean;
	onActivate: () => void;
}) {
	const positionClass =
		direction === "left"
			? "left-2.5 top-1/2 -translate-x-1/2 -translate-y-1/2"
			: direction === "right"
				? "right-2.5 top-1/2 translate-x-1/2 -translate-y-1/2"
				: "left-1/2 top-0.5 -translate-x-1/2 -translate-y-1/2";

	const hoverPopClass = reducedMotion
		? ""
		: direction === "left"
			? "group-hover/magic-edge:-translate-x-[calc(50%+0.5rem)] group-hover/magic-edge:-translate-y-1/2"
			: direction === "right"
				? "group-hover/magic-edge:translate-x-[calc(50%+0.5rem)] group-hover/magic-edge:-translate-y-1/2"
				: "group-hover/magic-edge:-translate-x-1/2 group-hover/magic-edge:-translate-y-[calc(50%+0.5rem)]";

	const Icon =
		direction === "left"
			? ArrowLeft
			: direction === "right"
				? ArrowRight
				: ArrowUp;
	const label =
		direction === "left"
			? "Select left card"
			: direction === "right"
				? "Select right card"
				: "Select middle card";

	return (
		<div
			className={cn(
				"pointer-events-auto absolute z-30",
				positionClass,
				"transition-transform duration-200 ease-out will-change-transform",
				hoverPopClass,
			)}
		>
			<CircleButton
				type="button"
				size="sm"
				chrome="solid"
				tone="light"
				aria-label={label}
				disabled={disabled}
				onClick={(event) => {
					event.preventDefault();
					event.stopPropagation();
					if (disabled) {
						return;
					}
					onActivate();
				}}
			>
				<Icon aria-hidden />
			</CircleButton>
		</div>
	);
}

function normalizeRotationDeg(index: number, count: number): number {
	if (count <= 1) {
		return 0;
	}
	// Symmetric fan around 0deg; large enough to read as a fan when tiles share one grid cell.
	const max = 8;
	const mid = (count - 1) / 2;
	const t = (index - mid) / Math.max(1, mid);
	return t * max;
}

/**
 * Horizontal fan offsets — sized from container width so tiles separate clearly without clipping.
 * Outer indices use ±max|t|·step; step is capped so 2·max|t|·step + cardWidth fits the section.
 * Compact/medium use a tighter cluster so smaller tiles sit closer together.
 */
function normalizeOffsetX(
	index: number,
	count: number,
	containerWidthPx: number,
	fanSize: MagicCardsFanSize = "wide",
): number {
	if (count <= 1) {
		return 0;
	}
	const w = Math.max(containerWidthPx, 280);
	const mid = (count - 1) / 2;
	const t = index - mid;
	const approxCardW =
		fanSize === "compact"
			? Math.min(204, Math.round(w * 0.48))
			: fanSize === "medium"
				? Math.min(240, Math.round(w * 0.38))
				: Math.min(288, Math.round(w * 0.3));
	const horizontalMargin =
		fanSize === "compact" ? 8 : fanSize === "medium" ? 12 : 22;
	const usable = Math.max(0, w - approxCardW - horizontalMargin);
	const maxAbsT = Math.max(Math.abs(0 - mid), Math.abs(count - 1 - mid));
	const cluster =
		fanSize === "compact" ? 0.42 : fanSize === "medium" ? 0.58 : 1;
	const stepCap =
		fanSize === "compact" ? 120 : fanSize === "medium" ? 180 : 316;
	const stepFloor =
		fanSize === "compact" ? 36 : fanSize === "medium" ? 52 : 80;
	const step = Math.min(
		stepCap,
		Math.max(
			stepFloor,
			maxAbsT > 1e-6
				? (usable / (2 * maxAbsT)) * cluster
				: usable * cluster,
		),
	);
	return Math.round(t * step);
}

/** Center index highest z-index so the middle card stays clickable (was idx+1 → last card ate hits). */
function fanStackZIndex(index: number, count: number): number {
	if (count <= 1) {
		return 10;
	}
	const mid = (count - 1) / 2;
	const dist = Math.abs(index - mid);
	return 30 - Math.round(dist * 10);
}

/** Vertical arc offset — matches `.magic-cards-layout--fan .magic-card-wrap` inline `dy`. */
function arcDy(index: number, count: number): number {
	if (count <= 1) {
		return 0;
	}
	const mid = (count - 1) / 2;
	const t = mid === 0 ? 0 : 1 - Math.abs((index - mid) / mid);
	return -Math.round(t * ARC_PEAK_PX);
}

/** Deck stack Y: middle slot dy; even counts average the two central dys. */
function middleDeckDy(count: number): number {
	if (count <= 1) {
		return arcDy(0, count);
	}
	const mid = (count - 1) / 2;
	const i0 = Math.floor(mid);
	const i1 = Math.ceil(mid);
	if (i0 === i1) {
		return arcDy(i0, count);
	}
	return Math.round((arcDy(i0, count) + arcDy(i1, count)) / 2);
}

/** Start offsets for narrow column morph — symmetric around centre like a vertical stack. */
function narrowColumnMorphOffsetY(index: number, count: number): number {
	if (count <= 1) {
		return 0;
	}
	const mid = (count - 1) / 2;
	return Math.round((index - mid) * NARROW_COLUMN_MORPH_PITCH_PX);
}

export function MagicCards({
	cards,
	density = "hub",
	ribbonAxis = "horizontal",
	dimUnselectedOnClick = false,
	onCardActivate,
	focusResetKey,
	enableSpotlight = true,
	enableBorderGlow = true,
	enableTilt = false,
	enableMagnetism = false,
	enableParticles = true,
	particleCount = DEFAULT_PARTICLE_COUNT,
	disableAnimations = false,
	glowColor = DEFAULT_GLOW_COLOR,
	glowColorDark = DEFAULT_GLOW_COLOR_DARK,
	spotlightRadius = DEFAULT_SPOTLIGHT_RADIUS,
	className,
	fanExpandKey,
	deckCollapseKey,
	initialCollapsedSelectionId,
	onDeckFocusChange,
	enableEdgeSelectHints = false,
	enableArrowKeySelect,
	carouselMode = "infinite",
}: MagicCardsProps) {
	const rootRef = useRef<HTMLDivElement>(null);
	const fanExpandSeenWhileFanRef = useRef(0);
	const fanExpandKeyRef = useRef(fanExpandKey);
	fanExpandKeyRef.current = fanExpandKey;
	const deckCollapseSeenRef = useRef(deckCollapseKey ?? 0);
	const onDeckFocusChangeRef = useRef(onDeckFocusChange);
	onDeckFocusChangeRef.current = onDeckFocusChange;
	const prevFocusResetKeyRef = useRef<string | undefined>(undefined);
	const skipInitialCollapseAfterFanRef = useRef(false);
	const prevInitialCollapsedSelectionIdRef = useRef<
		string | null | undefined
	>(undefined);

	const [containerWidth, setContainerWidth] = useState<number>(0);
	/** Hide deck until container width is measured — avoids wrong fan/stack CSS + white-card flash at width 0. */
	const [layoutReady, setLayoutReady] = useState(false);
	/** Shared min-height so every fan card matches the tallest in the row. */
	const [fanEqualCardMinHeightPx, setFanEqualCardMinHeightPx] = useState<
		number | null
	>(null);
	const isMobile = useMobileDetection();
	const isDark = useDocumentDarkClass();
	const prefersReducedMotionUi = useReducedMotion();
	const shouldDisableAnimations = disableAnimations || isMobile;
	const deckCssMotionReduced =
		Boolean(prefersReducedMotionUi) || Boolean(disableAnimations);

	const [focusIndex, setFocusIndex] = useState<number | null>(null);
	const [deckCollapsed, setDeckCollapsed] = useState(false);
	const [deckSelectedIndex, setDeckSelectedIndex] = useState<number | null>(
		null,
	);
	const [narrowDeckMotionToEnd, setNarrowDeckMotionToEnd] = useState(false);
	const [hoverIndex, setHoverIndex] = useState<number | null>(null);
	const [carouselCenterIndex, setCarouselCenterIndex] = useState(() =>
		resolveMagicCardsCarouselCenterIndex(cards.length),
	);
	const prevDeckCollapsedRef = useRef(deckCollapsed);

	const isCardHoverEligible = useCallback(
		(index: number): boolean => {
			if (deckCollapsed) {
				return false;
			}
			const card = cards[index];
			return Boolean(card && !card.disabled && !card.loading);
		},
		[cards, deckCollapsed],
	);

	/**
	 * Sticky mouseenter/leave hover (no mid-card flipping). After leave, the
	 * raised z-index is flushed sync so `elementsFromPoint` can see the card
	 * underneath (side → center handoff when overlap stole hit-testing).
	 */
	const resolveHoverIndexFromPoint = useCallback(
		(
			clientX: number,
			clientY: number,
			excludeIndex?: number,
		): number | null => {
			if (deckCollapsed) {
				return null;
			}
			const root = rootRef.current;
			if (!root || typeof document === "undefined") {
				return null;
			}
			const hits = document.elementsFromPoint(clientX, clientY);
			for (const hit of hits) {
				if (!(hit instanceof Element) || !root.contains(hit)) {
					continue;
				}
				const wrap = hit.closest("[data-magic-card-wrap-index]");
				if (!(wrap instanceof HTMLElement) || !root.contains(wrap)) {
					continue;
				}
				const idx = Number(wrap.dataset.magicCardWrapIndex);
				if (!Number.isFinite(idx) || idx < 0 || idx >= cards.length) {
					continue;
				}
				if (excludeIndex != null && idx === excludeIndex) {
					continue;
				}
				if (!isCardHoverEligible(idx)) {
					continue;
				}
				return idx;
			}
			return null;
		},
		[cards.length, deckCollapsed, isCardHoverEligible],
	);

	const handleCardMouseEnter = useCallback(
		(index: number) => {
			if (!isCardHoverEligible(index)) {
				return;
			}
			setHoverIndex(index);
		},
		[isCardHoverEligible],
	);

	const handleCardMouseLeave = useCallback(
		(index: number, event: React.MouseEvent<HTMLDivElement>) => {
			const related = event.relatedTarget;
			if (related instanceof Element) {
				const relatedWrap = related.closest(
					"[data-magic-card-wrap-index]",
				);
				if (relatedWrap instanceof HTMLElement) {
					const relatedIdx = Number(
						relatedWrap.dataset.magicCardWrapIndex,
					);
					if (
						Number.isFinite(relatedIdx) &&
						relatedIdx !== index &&
						isCardHoverEligible(relatedIdx)
					) {
						setHoverIndex(relatedIdx);
						return;
					}
				}
			}

			const { clientX, clientY } = event;
			flushSync(() => {
				setHoverIndex((prev) => (prev === index ? null : prev));
			});
			const underneath = resolveHoverIndexFromPoint(
				clientX,
				clientY,
				index,
			);
			if (underneath != null) {
				setHoverIndex(underneath);
			}
		},
		[isCardHoverEligible, resolveHoverIndexFromPoint],
	);

	useEffect(() => {
		const cb = onDeckFocusChangeRef.current;
		if (!cb) {
			return;
		}
		if (!deckCollapsed || deckSelectedIndex === null) {
			cb(null);
			return;
		}
		cb(cards[deckSelectedIndex] ?? null);
	}, [deckCollapsed, deckSelectedIndex, cards]);

	useEffect(() => {
		const key = focusResetKey ?? "";
		const prev = prevFocusResetKeyRef.current;
		if (prev === undefined) {
			prevFocusResetKeyRef.current = key;
			fanExpandSeenWhileFanRef.current = fanExpandKeyRef.current ?? 0;
			return;
		}
		if (prev === key) {
			return;
		}
		prevFocusResetKeyRef.current = key;
		skipInitialCollapseAfterFanRef.current = true;
		setFocusIndex(null);
		setDeckCollapsed(false);
		setDeckSelectedIndex(null);
		setNarrowDeckMotionToEnd(false);
		setHoverIndex(null);
		setCarouselCenterIndex(
			resolveMagicCardsCarouselCenterIndex(cards.length),
		);
		fanExpandSeenWhileFanRef.current = fanExpandKeyRef.current ?? 0;
	}, [focusResetKey, cards.length]);

	const canParticles =
		enableParticles &&
		!shouldDisableAnimations &&
		density !== "settingsCompact";
	const isSettingsCompact = density === "settingsCompact";
	/**
	 * Horizontal fan/deck at all breakpoints when `ribbonAxis="horizontal"` (default).
	 * Vertical ribbon keeps the legacy narrow column until the wide threshold.
	 */
	const isHorizontalFanLayout =
		!isSettingsCompact &&
		(ribbonAxis === "horizontal" ||
			containerWidth >= MAGIC_CARDS_FAN_MIN_PX);
	const fanSize = resolveMagicCardsFanSize(containerWidth);
	const showCarouselNav =
		isHorizontalFanLayout &&
		resolveMagicCardsCarouselNavVisible(
			fanSize,
			cards.length,
			deckCollapsed,
		);

	useEffect(() => {
		setCarouselCenterIndex((prev) => {
			if (cards.length <= 1) {
				return 0;
			}
			if (prev < 0 || prev >= cards.length) {
				return resolveMagicCardsCarouselCenterIndex(cards.length);
			}
			return prev;
		});
	}, [cards.length]);

	useEffect(() => {
		if (deckCollapsed) {
			setHoverIndex(null);
		}
	}, [deckCollapsed]);

	useLayoutEffect(() => {
		const wasCollapsed = prevDeckCollapsedRef.current;
		prevDeckCollapsedRef.current = deckCollapsed;

		if (!deckCollapsed || isHorizontalFanLayout) {
			setNarrowDeckMotionToEnd(false);
			return;
		}

		if (deckSelectedIndex === null) {
			return;
		}

		if (deckCssMotionReduced) {
			setNarrowDeckMotionToEnd(true);
			return;
		}

		const enteringCollapsed = deckCollapsed && !wasCollapsed;
		if (enteringCollapsed) {
			setNarrowDeckMotionToEnd(false);
			let raf1 = 0;
			let raf2 = 0;
			raf1 = requestAnimationFrame(() => {
				raf2 = requestAnimationFrame(() =>
					setNarrowDeckMotionToEnd(true),
				);
			});
			return () => {
				cancelAnimationFrame(raf1);
				cancelAnimationFrame(raf2);
			};
		}

		setNarrowDeckMotionToEnd(true);
	}, [
		deckCollapsed,
		deckCssMotionReduced,
		deckSelectedIndex,
		isHorizontalFanLayout,
	]);

	useLayoutEffect(() => {
		const raw = initialCollapsedSelectionId;
		const id =
			typeof raw === "string" && raw.trim().length > 0
				? raw.trim()
				: null;
		const prevId = prevInitialCollapsedSelectionIdRef.current;

		if (id !== prevId) {
			prevInitialCollapsedSelectionIdRef.current = id;
			if (prevId !== undefined) {
				skipInitialCollapseAfterFanRef.current = false;
			}
		}

		if (!id) {
			return;
		}
		if (skipInitialCollapseAfterFanRef.current) {
			return;
		}

		const idx = cards.findIndex((c) => c.id === id);
		if (idx < 0) {
			return;
		}
		if (cards[idx]?.disabled) {
			return;
		}

		const sectionW =
			rootRef.current?.getBoundingClientRect().width ?? containerWidth;
		if (cards.length > 1 && sectionW <= 0) {
			return;
		}

		if (deckCollapsed && deckSelectedIndex === idx) {
			return;
		}

		if (dimUnselectedOnClick) {
			setFocusIndex(idx);
		}
		setDeckSelectedIndex(idx);
		setDeckCollapsed(true);
	}, [
		initialCollapsedSelectionId,
		cards,
		containerWidth,
		deckCollapsed,
		deckSelectedIndex,
		dimUnselectedOnClick,
	]);

	const activateCard = useCallback(
		(index: number) => {
			const card = cards[index];
			if (!card || card.disabled) {
				return;
			}
			onCardActivate?.(index, card);

			if (dimUnselectedOnClick) {
				setFocusIndex(index);
			}

			if (cards.length <= 1) {
				return;
			}

			const sectionW =
				rootRef.current?.getBoundingClientRect().width ??
				containerWidth;
			const layoutIsHorizontal =
				ribbonAxis === "horizontal" ||
				sectionW >= MAGIC_CARDS_FAN_MIN_PX;

			if (!layoutIsHorizontal) {
				if (deckCollapsed) {
					if (deckSelectedIndex === index) {
						return;
					}
					setDeckSelectedIndex(index);
					return;
				}
				setDeckSelectedIndex(index);
				setDeckCollapsed(true);
				return;
			}

			if (deckCollapsed) {
				if (deckSelectedIndex === index) {
					return;
				}
				setDeckSelectedIndex(index);
				return;
			}

			setDeckSelectedIndex(index);
			setDeckCollapsed(true);
		},
		[
			cards,
			containerWidth,
			deckCollapsed,
			deckSelectedIndex,
			dimUnselectedOnClick,
			onCardActivate,
			ribbonAxis,
		],
	);

	const showCarouselNavActive = showCarouselNav;
	const showEdgeSelectHints =
		enableEdgeSelectHints &&
		!showCarouselNavActive &&
		!isSettingsCompact &&
		!deckCollapsed &&
		cards.length >= 2;
	const arrowKeySelectEnabled =
		(enableArrowKeySelect ?? enableEdgeSelectHints) &&
		!showCarouselNavActive &&
		!isSettingsCompact &&
		!deckCollapsed &&
		cards.length >= 2;

	const stepCarousel = useCallback(
		(delta: -1 | 1) => {
			if (!showCarouselNavActive || cards.length < 2) {
				return;
			}
			setCarouselCenterIndex((prev) =>
				stepMagicCardsCarouselCenter(
					prev,
					cards.length,
					delta,
					carouselMode,
				),
			);
		},
		[cards.length, carouselMode, showCarouselNavActive],
	);

	const canCarouselStepLeft =
		carouselMode === "infinite" || carouselCenterIndex > 0;
	const canCarouselStepRight =
		carouselMode === "infinite" ||
		carouselCenterIndex < Math.max(0, cards.length - 1);

	useEffect(() => {
		if (!showCarouselNavActive && !arrowKeySelectEnabled) {
			return;
		}
		const onKeyDown = (event: KeyboardEvent) => {
			if (
				event.defaultPrevented ||
				event.metaKey ||
				event.ctrlKey ||
				event.altKey
			) {
				return;
			}
			const target = event.target;
			if (
				target instanceof HTMLElement &&
				target.closest(
					"input, textarea, select, [contenteditable=true]",
				)
			) {
				return;
			}

			if (showCarouselNavActive) {
				if (event.key === "ArrowLeft") {
					if (!canCarouselStepLeft) {
						return;
					}
					event.preventDefault();
					stepCarousel(-1);
					return;
				}
				if (event.key === "ArrowRight") {
					if (!canCarouselStepRight) {
						return;
					}
					event.preventDefault();
					stepCarousel(1);
					return;
				}
				return;
			}

			if (!arrowKeySelectEnabled) {
				return;
			}
			let hint: MagicCardsEdgeSelectHint | null = null;
			if (event.key === "ArrowLeft") {
				hint = "left";
			} else if (event.key === "ArrowRight") {
				hint = "right";
			} else if (event.key === "ArrowUp") {
				hint = "up";
			} else {
				return;
			}
			const index = edgeSelectHintIndex(hint, cards.length);
			if (index == null) {
				return;
			}
			event.preventDefault();
			activateCard(index);
		};
		window.addEventListener("keydown", onKeyDown);
		return () => window.removeEventListener("keydown", onKeyDown);
	}, [
		activateCard,
		arrowKeySelectEnabled,
		canCarouselStepLeft,
		canCarouselStepRight,
		cards.length,
		showCarouselNavActive,
		stepCarousel,
	]);

	useEffect(() => {
		if (fanExpandKey === undefined) {
			return;
		}

		if (!deckCollapsed) {
			fanExpandSeenWhileFanRef.current = fanExpandKey;
			return;
		}

		if (fanExpandKey <= fanExpandSeenWhileFanRef.current) {
			return;
		}

		fanExpandSeenWhileFanRef.current = fanExpandKey;
		setDeckCollapsed(false);
		setDeckSelectedIndex(null);
		setFocusIndex(null);
		setHoverIndex(null);
		setCarouselCenterIndex(
			resolveMagicCardsCarouselCenterIndex(cards.length),
		);
	}, [fanExpandKey, deckCollapsed, cards.length]);

	useEffect(() => {
		if (deckCollapseKey === undefined) {
			return;
		}
		if (deckCollapseKey <= deckCollapseSeenRef.current) {
			return;
		}
		deckCollapseSeenRef.current = deckCollapseKey;
		if (cards.length <= 1) {
			return;
		}
		/**
		 * Programmatic collapse (boost reroll, etc.) — same morph as `activateCard`.
		 * Do **not** bail on `card.disabled`: callers often lock interaction during the ritual.
		 */
		const centerIdx = resolveMagicCardsCarouselCenterIndex(cards.length);
		if (dimUnselectedOnClick) {
			setFocusIndex(centerIdx);
		}
		setDeckSelectedIndex(centerIdx);
		setDeckCollapsed(true);
		setHoverIndex(null);
	}, [deckCollapseKey, cards.length, dimUnselectedOnClick]);

	const useCarouselSlots =
		isHorizontalFanLayout &&
		(fanSize === "compact" || fanSize === "medium") &&
		cards.length >= 2;

	const layoutMeta = useMemo(() => {
		const n = cards.length;
		const cw = isHorizontalFanLayout
			? Math.max(containerWidth, 280)
			: Math.max(containerWidth, MAGIC_CARDS_FAN_MIN_PX);
		const sizeForOffsets = isHorizontalFanLayout ? fanSize : "wide";
		return cards.map((_, idx) => {
			const slot = useCarouselSlots
				? resolveMagicCardsCarouselVisualSlot(
						idx,
						carouselCenterIndex,
						n,
					)
				: idx;
			return {
				rot: normalizeRotationDeg(slot, n),
				dx: normalizeOffsetX(slot, n, cw, sizeForOffsets),
				dy: arcDy(slot, n),
				z: fanStackZIndex(slot, n),
			};
		});
	}, [
		cards,
		carouselCenterIndex,
		containerWidth,
		fanSize,
		isHorizontalFanLayout,
		useCarouselSlots,
	]);

	const deckLayerTransition = useMemo(
		() =>
			deckCssMotionReduced
				? `transform ${DECK_TRANSFORM_MS_REDUCED}ms ease-out, opacity ${Math.round(DECK_TRANSFORM_MS_REDUCED * 0.65)}ms ease`
				: `transform ${DECK_TRANSFORM_MS}ms cubic-bezier(0.22, 1, 0.36, 1), opacity ${Math.round(DECK_TRANSFORM_MS * 0.55)}ms ease`,
		[deckCssMotionReduced],
	);
	const fanPoseTransition = useMemo(
		() =>
			deckCssMotionReduced
				? `transform ${CAROUSEL_TRANSFORM_MS_REDUCED}ms ease-out, opacity ${Math.round(CAROUSEL_TRANSFORM_MS_REDUCED * 0.7)}ms ease`
				: `transform ${CAROUSEL_TRANSFORM_MS}ms ${CAROUSEL_TRANSFORM_EASE}, opacity ${Math.round(CAROUSEL_TRANSFORM_MS * 0.7)}ms ease`,
		[deckCssMotionReduced],
	);

	useLayoutEffect(() => {
		if (!rootRef.current) {
			return;
		}
		const el = rootRef.current;
		const read = () => {
			const w = el.getBoundingClientRect().width;
			setContainerWidth(w);
			if (w > 0) {
				setLayoutReady(true);
			}
		};
		read();
		const ro = new ResizeObserver(() => read());
		ro.observe(el);
		return () => ro.disconnect();
	}, []);

	/**
	 * Fan tiles share one grid cell (transforms only) — CSS stretch cannot equalize
	 * heights. Measure intrinsic card heights and set a shared min-height.
	 */
	useLayoutEffect(() => {
		if (
			!layoutReady ||
			!isHorizontalFanLayout ||
			deckCollapsed ||
			isSettingsCompact ||
			cards.length < 2
		) {
			setFanEqualCardMinHeightPx(null);
			return;
		}
		const root = rootRef.current;
		if (!root) {
			return;
		}

		const measure = () => {
			const surfaces = [
				...root.querySelectorAll<HTMLElement>(".magic-card"),
			];
			if (surfaces.length < 2) {
				setFanEqualCardMinHeightPx(null);
				return;
			}
			let max = 0;
			for (const surface of surfaces) {
				const inner =
					surface.querySelector<HTMLElement>(".magic-card-cq") ??
					surface;
				max = Math.max(max, inner.scrollHeight);
			}
			const next = Math.ceil(max);
			if (next <= 0) {
				setFanEqualCardMinHeightPx(null);
				return;
			}
			setFanEqualCardMinHeightPx((prev) => (prev === next ? prev : next));
		};

		measure();
		const ro = new ResizeObserver(() => {
			measure();
		});
		for (const surface of root.querySelectorAll(".magic-card")) {
			ro.observe(surface);
		}
		return () => {
			ro.disconnect();
		};
	}, [
		cards,
		carouselCenterIndex,
		containerWidth,
		deckCollapsed,
		fanSize,
		isHorizontalFanLayout,
		isSettingsCompact,
		layoutReady,
	]);

	return (
		<div className={cn("flex min-h-0 w-full flex-1 flex-col", className)}>
			<style>
				{`
          .magic-cards-section {
            --magic-card-radius: 24px;
            container-type: inline-size;
            height: 100%;
            min-height: 0;
            display: flex;
            flex-direction: column;
            flex: 1 1 0%;
          }

          /* Nested CQ so intra-card breakpoints track each card width (not the whole strip). */
          .magic-card-cq {
            container-type: inline-size;
            width: 100%;
            min-height: 0;
            display: flex;
            flex-direction: column;
          }

          .magic-card .magic-card__surface {
            position: relative;
            z-index: 2;
          }

          /* Layout: vertical stack only when ribbonAxis=vertical + narrow.
             Horizontal fan (default) uses --fan at all breakpoints with size tiers. */
          .magic-cards-layout {
            display: flex;
            flex-direction: column;
            gap: 12px;
            flex: 1 1 auto;
            width: 100%;
            min-height: 0;
            justify-content: center;
            align-items: center;
          }

          .magic-cards-layout.magic-cards-layout--stack {
            overflow-y: auto;
            overscroll-behavior: contain;
          }

          .magic-cards-layout .magic-card-wrap {
            flex: 0 0 auto;
            display: flex;
            width: 100%;
            max-width: 100%;
            justify-content: center;
          }

          /* Narrow deck: same single-origin grid + tile width cap as wide fan/deck (section CQ). */
          .magic-cards-layout.magic-cards-layout--narrow-deck {
            display: grid;
            grid-template-columns: 1fr;
            grid-template-rows: 1fr;
            justify-items: center;
            align-items: center;
            align-content: center;
            gap: 0;
            padding: 8px 0;
            min-height: 0;
            overflow: hidden;
          }
          .magic-cards-layout.magic-cards-layout--narrow-deck .magic-card-wrap {
            grid-column: 1;
            grid-row: 1;
            margin-left: 0 !important;
            flex: none !important;
            width: min(18rem, calc(100cqw - 1.5rem));
            max-width: min(100%, 18rem);
          }

          /* settingsCompact: 3-up @ ~375px; 6 options → 2×3 (P6-AC1/AC2). */
          .magic-cards-layout.magic-cards-layout--settings-compact {
            display: grid;
            grid-template-columns: repeat(3, minmax(0, 1fr));
            gap: 8px;
            align-items: stretch;
            justify-items: stretch;
            padding: 4px 0;
            overflow-y: auto;
            overscroll-behavior: contain;
          }
          .magic-cards-layout.magic-cards-layout--settings-compact .magic-card-wrap {
            width: 100%;
            max-width: 100%;
            margin-left: 0 !important;
            flex: none !important;
          }
          .magic-cards-layout.magic-cards-layout--settings-compact .magic-card {
            min-height: 0;
            --magic-card-radius: 16px;
          }
          .magic-cards-layout.magic-cards-layout--settings-compact .magic-card__title,
          .magic-cards-layout.magic-cards-layout--settings-compact .magic-card__label {
            font-size: 0.75rem;
            line-height: 1.2;
            overflow-wrap: anywhere;
            hyphens: auto;
          }
          .magic-cards-layout.magic-cards-layout--settings-compact.magic-cards-layout--deck-active {
            display: grid;
            grid-template-columns: 1fr;
            grid-template-rows: 1fr;
            justify-items: center;
            gap: 0;
          }
          .magic-cards-layout.magic-cards-layout--settings-compact.magic-cards-layout--deck-active .magic-card-wrap {
            grid-column: 1;
            grid-row: 1;
            width: min(100%, 11rem);
          }

          /* Horizontal fan + deck: one grid cell so fan offsets are pure transforms.
             Size tiers (data-fan-size) scale tile chrome for completion overlay + hubs. */
          .magic-cards-layout.magic-cards-layout--fan {
            display: grid;
            grid-template-columns: 1fr;
            grid-template-rows: 1fr;
            justify-items: center;
            align-items: center;
            align-content: center;
            flex-direction: unset;
            gap: 0;
            padding: 8px 0;
            min-height: 0;
            overflow: visible;
          }
          .magic-cards-layout.magic-cards-layout--fan .magic-card-wrap {
            grid-column: 1;
            grid-row: 1;
            margin-left: 0 !important;
            flex: none !important;
            /* Wraps share one grid cell; only the transformed card may hit-test
               or a raised side wrap’s empty center box steals center hover. */
            pointer-events: none;
          }
          .magic-cards-layout.magic-cards-layout--fan .magic-card-transform-layer {
            pointer-events: none;
          }
          .magic-cards-layout.magic-cards-layout--fan .magic-card-transform-layer .magic-card,
          .magic-cards-layout.magic-cards-layout--fan .magic-card-transform-layer .pointer-events-auto {
            pointer-events: auto;
          }
          .magic-cards-layout.magic-cards-layout--fan[data-fan-size="wide"] .magic-card-wrap {
            width: min(18rem, 32cqw);
            max-width: min(100%, 18rem);
          }
          .magic-cards-layout.magic-cards-layout--fan[data-fan-size="medium"] .magic-card-wrap {
            width: min(15rem, 40cqw);
            max-width: min(100%, 15rem);
          }
          .magic-cards-layout.magic-cards-layout--fan[data-fan-size="compact"] .magic-card-wrap {
            width: min(12.75rem, 52cqw);
            max-width: min(100%, 12.75rem);
          }
          .magic-cards-layout.magic-cards-layout--fan[data-fan-size="medium"] .magic-card {
            --magic-card-radius: 20px;
            min-height: 140px;
          }
          .magic-cards-layout.magic-cards-layout--fan[data-fan-size="compact"] .magic-card {
            --magic-card-radius: 16px;
            min-height: 120px;
          }

          /*
           * Torch surfaces default to overflow:visible (glow). Clip hub cards to
           * their radius so fill never squares past the glass ring. Fan rarity
           * banners need overflow:visible so FlagBanner can sit on the top edge;
           * the in-flow body column clips with MAGIC_CARD_INNER_CLIP_STYLE.
           */
          .magic-torch-root.magic-cards-section .magic-torch-surface.magic-card {
            overflow: hidden;
          }
          .magic-torch-root.magic-cards-section .magic-torch-surface.magic-card.magic-card--flag-fan {
            overflow: visible;
          }
          /*
           * Glass + glow rings must paint ABOVE plate/content. Torch defaults put
           * children at z-index 3 and the ring at 1, so square fills covered the
           * frosted curve. Keep the ring on top of the 6px band only.
           */
          .magic-torch-root.magic-cards-section .glass-border-ring-host.magic-card::before {
            z-index: 24;
          }
          .magic-torch-root.magic-cards-section .magic-torch-surface.magic-card.magic-torch-surface--border-glow::after {
            z-index: 25;
          }
          /*
           * Beat torch direct-child z-index:3 so rarity FlagBanners sit above glass (24/25).
           */
          .magic-torch-root.magic-cards-section .magic-torch-surface.magic-card > .magic-card__flag-layer {
            z-index: 30;
          }
          .magic-torch-root.magic-cards-section .magic-card__plate {
            overflow: hidden;
          }
          .magic-cards-layout.magic-cards-layout--fan[data-fan-size="medium"] .magic-card__title,
          .magic-cards-layout.magic-cards-layout--fan[data-fan-size="medium"] .magic-card__label {
            font-size: 1.25rem;
            line-height: 1.25;
          }
          .magic-cards-layout.magic-cards-layout--fan[data-fan-size="compact"] .magic-card__title,
          .magic-cards-layout.magic-cards-layout--fan[data-fan-size="compact"] .magic-card__label {
            font-size: 1rem;
            line-height: 1.2;
          }

          .magic-torch-root.magic-cards-section .magic-cards-layout--deck-active .magic-torch-surface.magic-card:hover {
            transform: translate3d(0, 0, 0);
          }

          .magic-torch-root.magic-cards-section .magic-cards-layout--deck-active .magic-torch-surface.magic-card[data-active] {
            transform: translate3d(0, 0, 0);
          }

          .magic-torch-root.magic-cards-section .magic-cards-layout--deck-active .magic-torch-surface.magic-card {
            transition:
              box-shadow 0.28s ease,
              border-color 0.28s ease;
          }

          /*
           * Hero / tile media: muted grayscale until hover, or carousel-center /
           * data-active when no sibling card is hovered.
           */
          .magic-torch-root.magic-cards-section .magic-card-hover-colorize {
            filter: grayscale(1);
            transition: filter 0.3s ease-out;
          }
          .magic-torch-root.magic-cards-section .magic-card:hover .magic-card-hover-colorize,
          .magic-torch-root.magic-cards-section .magic-card:focus-visible .magic-card-hover-colorize,
          .magic-torch-root.magic-cards-section .magic-card:focus-within .magic-card-hover-colorize {
            filter: grayscale(0);
          }
          .magic-torch-root.magic-cards-section .magic-cards-layout:not(:has(.magic-card:hover)) .magic-card-wrap[data-carousel-center] .magic-card-hover-colorize,
          .magic-torch-root.magic-cards-section .magic-cards-layout:not(:has(.magic-card:hover)) .magic-card[data-active] .magic-card-hover-colorize {
            filter: grayscale(0);
          }
          .magic-torch-root.magic-cards-section .magic-cards-layout:not(:has(.magic-card:hover)) .magic-card-wrap[data-carousel-center] .magic-card-img-fade {
            opacity: 1;
          }

          /*
           * Scale pulse gated like colorize: idle at pulse-scale min; animate only
           * on hover / focus / carousel-center / data-active (no sibling hover).
           */
          .magic-torch-root.magic-cards-section .magic-card-pulse-scale {
            transform: scale(0.86);
            transform-origin: center;
          }
          @media (prefers-reduced-motion: no-preference) {
            .magic-torch-root.magic-cards-section .magic-card:hover .magic-card-pulse-scale,
            .magic-torch-root.magic-cards-section .magic-card:focus-visible .magic-card-pulse-scale,
            .magic-torch-root.magic-cards-section .magic-card:focus-within .magic-card-pulse-scale,
            .magic-torch-root.magic-cards-section .magic-cards-layout:not(:has(.magic-card:hover)) .magic-card-wrap[data-carousel-center] .magic-card-pulse-scale,
            .magic-torch-root.magic-cards-section .magic-cards-layout:not(:has(.magic-card:hover)) .magic-card[data-active] .magic-card-pulse-scale {
              animation: pulse-scale var(--pulse-scale-duration, 3.6s) ease-in-out infinite;
              will-change: transform;
            }
          }

          /* Transform layer animates fan/deck pose; inner card hover stays independent at rest. */
          .magic-card-transform-layer:not(.magic-card-transform-layer--motion-reduced) {
            will-change: transform, opacity;
          }
        `}
			</style>

			<MagicTorch
				torchRootRef={rootRef}
				torchRootAttributes={{
					"aria-busy": !layoutReady,
					"aria-hidden": !layoutReady,
				}}
				className={cn(
					"magic-cards-section relative w-full",
					layoutReady ? "visible" : "invisible pointer-events-none",
				)}
				enableSpotlight={
					enableSpotlight && layoutReady && !isSettingsCompact
				}
				disableAnimations={shouldDisableAnimations}
				glowColor={glowColor}
				glowColorDark={glowColorDark}
				spotlightRadius={spotlightRadius}
				surfaceBorderRadius={isSettingsCompact ? "16px" : "24px"}
				defaultEnableParticles={canParticles}
				defaultParticleCount={particleCount}
				defaultEnableTilt={enableTilt && !isSettingsCompact}
				defaultEnableMagnetism={enableMagnetism && !isSettingsCompact}
				defaultEnableBorderGlow={enableBorderGlow}
				spotlightElementClassName="magic-cards-global-spotlight"
			>
				<div
					className={cn(
						"magic-cards-layout",
						isSettingsCompact
							? "magic-cards-layout--settings-compact"
							: isHorizontalFanLayout
								? "magic-cards-layout--fan"
								: deckCollapsed
									? "magic-cards-layout--narrow-deck"
									: "magic-cards-layout--stack",
						deckCollapsed && "magic-cards-layout--deck-active",
					)}
					{...(isHorizontalFanLayout
						? { "data-fan-size": fanSize }
						: {})}
				>
					{cards.map((card, index) => {
						const meta = layoutMeta[index];
						const hideParticleDimOverlay = deckCollapsed;
						const isDimmed =
							dimUnselectedOnClick &&
							focusIndex != null &&
							focusIndex !== index &&
							!hideParticleDimOverlay;
						const isSurfaceActive =
							dimUnselectedOnClick &&
							focusIndex != null &&
							focusIndex === index;
						const title = card.title ?? card.label;
						const isLoading = Boolean(card.loading);
						const cardDisabled =
							Boolean(card.disabled) || isLoading;

						const hubSizeClass =
							fanSize === "compact"
								? "min-h-[120px] min-w-[11.5rem]"
								: fanSize === "medium"
									? "min-h-[140px] min-w-[13rem]"
									: "min-h-[160px] min-w-[275px]";

						const hasFanCornerFlag =
							Boolean(card.flags?.[0]) && isHorizontalFanLayout;

						const baseClassName = [
							"magic-card group relative flex w-full shrink-0",
							hasFanCornerFlag && "magic-card--flag-fan",
							isSettingsCompact
								? "max-h-[200px] min-h-[88px] min-w-0"
								: hubSizeClass,
							isSurfaceActive
								? "cursor-default"
								: cardDisabled
									? "cursor-not-allowed"
									: "cursor-pointer",
							/**
							 * Outer shell stays transparent so the glass ring isn’t muddied by
							 * plate fill. Opaque paint lives on `.magic-card__plate` inset by
							 * the ring width (see tileChrome).
							 */
							"flex-col overflow-hidden bg-transparent",
							isSettingsCompact || fanSize === "compact"
								? "rounded-2xl"
								: "rounded-3xl",
							"border border-solid border-transparent",
							"p-0",
							!isSettingsCompact &&
								fanSize === "wide" &&
								"@[740px]:mx-auto @[740px]:w-4/5",
						]
							.filter(Boolean)
							.join(" ");

						const resolvedCardGlow = cardDisabled
							? isDark
								? DISABLED_GLOW_COLOR_DARK
								: DISABLED_GLOW_COLOR
							: isDark
								? (card.glowColorDark ?? glowColorDark)
								: (card.glowColor ?? glowColor);

						const cardStyle = {
							backgroundColor: "transparent",
							...(isSurfaceActive ? { cursor: "default" } : {}),
							...(fanEqualCardMinHeightPx != null &&
							isHorizontalFanLayout &&
							!deckCollapsed
								? { minHeight: fanEqualCardMinHeightPx }
								: {}),
						} as React.CSSProperties;

						const nCards = cards.length;
						const selIdx = deckSelectedIndex;
						const isDeckPose =
							deckCollapsed &&
							selIdx !== null &&
							meta !== undefined;
						const narrowMorphFromColumn =
							!isHorizontalFanLayout &&
							isDeckPose &&
							!narrowDeckMotionToEnd &&
							!deckCssMotionReduced;

						let wrapOuterStyle: React.CSSProperties | undefined;
						let wrapInnerStyle: React.CSSProperties | undefined;

						if (!meta) {
							wrapOuterStyle = undefined;
							wrapInnerStyle = undefined;
						} else if (!isHorizontalFanLayout && isDeckPose) {
							const deckDy = middleDeckDy(nCards);
							const isTop = selIdx === index;
							wrapOuterStyle = {
								zIndex: isTop ? 50 : meta.z,
							};
							if (narrowMorphFromColumn) {
								wrapInnerStyle = {
									transform: `translate3d(0, ${narrowColumnMorphOffsetY(index, nCards)}px, 0) rotate(0deg)`,
									opacity: 1,
									transition: "none",
								};
							} else {
								wrapInnerStyle = {
									transform: `translate3d(0, ${deckDy}px, 0) rotate(0deg)`,
									opacity: isTop ? 1 : 0,
									transition: deckLayerTransition,
								};
							}
						} else if (isHorizontalFanLayout) {
							if (isDeckPose) {
								const deckDy = middleDeckDy(nCards);
								const isTop = selIdx === index;
								wrapOuterStyle = {
									zIndex: isTop ? 50 : meta.z,
								};
								wrapInnerStyle = {
									transform: `translate3d(0, ${deckDy}px, 0) rotate(0deg)`,
									opacity: isTop ? 1 : 0,
									transition: deckLayerTransition,
								};
							} else {
								const raised =
									hoverIndex === index && !deckCollapsed;
								wrapOuterStyle = {
									zIndex: raised ? FAN_HOVER_Z_INDEX : meta.z,
								};
								wrapInnerStyle = {
									transform: `translate3d(${meta.dx}px, ${meta.dy}px, 0) rotate(${meta.rot}deg)`,
									opacity: 1,
									transition: fanPoseTransition,
								};
							}
						} else {
							wrapOuterStyle = undefined;
							wrapInnerStyle = undefined;
						}

						const layoutFaceVariant =
							isHorizontalFanLayout || isDeckPose
								? "fan"
								: "stack";

						const cornerFlagPlacement: MagicCardListCornerFlagPlacement =
							isHorizontalFanLayout ? "fan" : "stack";

						const overlayNode =
							isLoading || card.overlay ? (
								<>
									{isLoading ? (
										<MagicCardLoadingOverlay />
									) : null}
									{card.overlay ? card.overlay : null}
								</>
							) : null;

						const edgeHint = showEdgeSelectHints
							? resolveMagicCardsEdgeSelectHint(index, nCards)
							: null;

						const tileChrome = (
							<MagicTorchSurface
								className={baseClassName}
								style={cardStyle}
								disabled={cardDisabled}
								active={isSurfaceActive}
								dimmed={isDimmed}
								glowColorRgb={resolvedCardGlow}
								particleGlowRgb={
									canParticles ? resolvedCardGlow : undefined
								}
								enableBorderGlow={enableBorderGlow}
								enableGlassBorderRing={enableBorderGlow}
								enableTilt={enableTilt}
								enableMagnetism={enableMagnetism}
								onActivate={() => activateCard(index)}
								wrapChildrenInSurfaceInner={false}
							>
								{/*
								 * Opaque plate inset to the glass ring’s inner edge so fill
								 * never paints under/through the frosted band.
								 */}
								<div
									aria-hidden
									className={cn(
										"magic-card__plate pointer-events-none",
										cardDisabled
											? "bg-white/20 dark:bg-[#141924]/20"
											: "bg-white dark:bg-[#141924]",
									)}
									style={MAGIC_CARD_PLATE_STYLE}
								/>
								{/*
								 * Flag must stack above glass ring (z 24) / glow (z 25).
								 * Cannot live under `.magic-card-cq` (z 1) or the ring wins.
								 */}
								{card.flags?.[0] ? (
									<div className="magic-card__flag-layer pointer-events-none absolute inset-0 overflow-visible">
										<MagicCardListCornerFlag
											flag={card.flags[0]}
											placement={cornerFlagPlacement}
										/>
									</div>
								) : null}
								<div className="magic-card-cq relative z-[1] flex min-h-0 w-full flex-1 flex-col">
									<MagicCardFace
										card={card}
										layoutVariant={layoutFaceVariant}
										flagPlacement={cornerFlagPlacement}
										showCornerFlag={false}
										fanSize={
											isHorizontalFanLayout
												? fanSize
												: "wide"
										}
										title={title}
										overlay={overlayNode}
										blurContent={
											isLoading || Boolean(card.overlay)
										}
									/>
								</div>
							</MagicTorchSurface>
						);

						const edgeHintNode =
							edgeHint != null ? (
								<MagicCardEdgeSelectHint
									direction={edgeHint}
									disabled={cardDisabled}
									reducedMotion={deckCssMotionReduced}
									onActivate={() => activateCard(index)}
								/>
							) : null;

						const useTransformShell =
							(Boolean(isHorizontalFanLayout) && Boolean(meta)) ||
							(Boolean(!isHorizontalFanLayout) &&
								isDeckPose &&
								Boolean(meta));

						const isCarouselCenter =
							useCarouselSlots && index === carouselCenterIndex;

						if (!useTransformShell) {
							return (
								<div
									key={card.id}
									data-magic-card-wrap-index={index}
									{...(isCarouselCenter
										? { "data-carousel-center": "" }
										: {})}
									className={cn(
										"magic-card-wrap min-w-0",
										edgeHintNode && "group/magic-edge",
										cardDisabled && "cursor-not-allowed",
									)}
								>
									{/* biome-ignore lint/a11y/noStaticElementInteractions: hover raises fan z-index only; activation stays on MagicTorchSurface */}
									<div
										className="relative min-h-0 min-w-0 w-full pointer-events-auto"
										onMouseEnter={() => {
											handleCardMouseEnter(index);
										}}
										onMouseLeave={(event) => {
											handleCardMouseLeave(index, event);
										}}
									>
										{tileChrome}
										{edgeHintNode}
									</div>
								</div>
							);
						}

						return (
							<div
								key={card.id}
								data-magic-card-wrap-index={index}
								{...(isCarouselCenter
									? { "data-carousel-center": "" }
									: {})}
								className={cn(
									"magic-card-wrap min-w-0",
									"relative",
									edgeHintNode && "group/magic-edge",
									cardDisabled && "cursor-not-allowed",
								)}
								style={wrapOuterStyle}
							>
								<div
									className={cn(
										"magic-card-transform-layer relative flex min-h-0 min-w-0 w-full justify-center",
										deckCssMotionReduced &&
											"magic-card-transform-layer--motion-reduced",
									)}
									style={wrapInnerStyle}
								>
									{/* biome-ignore lint/a11y/noStaticElementInteractions: hover raises fan z-index only; activation stays on MagicTorchSurface */}
									<div
										className="relative min-h-0 min-w-0 w-full pointer-events-auto"
										onMouseEnter={() => {
											handleCardMouseEnter(index);
										}}
										onMouseLeave={(event) => {
											handleCardMouseLeave(index, event);
										}}
									>
										{tileChrome}
										{edgeHintNode}
									</div>
								</div>
							</div>
						);
					})}
				</div>
			</MagicTorch>
			{showCarouselNavActive ? (
				<div className="relative z-10 flex shrink-0 items-center justify-center gap-3 pb-2 pt-3">
					<CircleButton
						type="button"
						size="sm"
						chrome="solid"
						tone="light"
						aria-label="Previous card"
						disabled={!canCarouselStepLeft}
						onClick={() => stepCarousel(-1)}
					>
						<ChevronLeft aria-hidden />
					</CircleButton>
					<CircleButton
						type="button"
						size="sm"
						chrome="solid"
						tone="light"
						aria-label="Next card"
						disabled={!canCarouselStepRight}
						onClick={() => stepCarousel(1)}
					>
						<ChevronRight aria-hidden />
					</CircleButton>
				</div>
			) : null}
		</div>
	);
}
